/**
 * Dinheiro sem ponto flutuante.
 *
 * No banco, valores são `numeric(19,4)`; na aplicação trafegam como texto ("1234.5600") e as
 * contas são feitas em bigint na escala de 4 casas. Nunca use `number` para dinheiro.
 */

export const AMOUNT_SCALE = 4;
const FACTOR = 10n ** BigInt(AMOUNT_SCALE);
const AMOUNT_RE = /^(-)?(\d+)(?:\.(\d+))?$/;

export interface Money {
  /** Valor em texto decimal, ex.: "19.9000". */
  amount: string;
  /** Código ISO 4217, ex.: "BRL". */
  currency: string;
}

/** Converte texto decimal em bigint na escala de 4 casas. Recusa mais de 4 casas. */
export function toScaled(amount: string): bigint {
  const m = AMOUNT_RE.exec(amount.trim());
  if (!m) throw new Error(`Valor inválido: "${amount}"`);
  const [, sign, int, frac = ''] = m;
  if (frac.length > AMOUNT_SCALE) {
    throw new Error(`Valor com mais de ${AMOUNT_SCALE} casas decimais: "${amount}"`);
  }
  const scaled = BigInt(int!) * FACTOR + BigInt(frac.padEnd(AMOUNT_SCALE, '0'));
  return sign ? -scaled : scaled;
}

/** Converte bigint na escala de 4 casas de volta para texto decimal. */
export function fromScaled(value: bigint): string {
  const negative = value < 0n;
  const abs = negative ? -value : value;
  const int = abs / FACTOR;
  const frac = (abs % FACTOR).toString().padStart(AMOUNT_SCALE, '0');
  return `${negative ? '-' : ''}${int}.${frac}`;
}

export function addAmounts(...amounts: string[]): string {
  return fromScaled(amounts.reduce((sum, a) => sum + toScaled(a), 0n));
}

/** Soma valores da mesma moeda; recusa misturar moedas. */
export function sumMoney(values: readonly Money[]): Money | null {
  if (values.length === 0) return null;
  const currency = values[0]!.currency;
  if (values.some((v) => v.currency !== currency)) {
    throw new Error('Não é possível somar valores de moedas diferentes sem converter');
  }
  return { amount: addAmounts(...values.map((v) => v.amount)), currency };
}

/** Formata para exibição conforme o idioma da pessoa. */
export function formatMoney(value: Money, locale: string): string {
  const formatter = new Intl.NumberFormat(locale, { style: 'currency', currency: value.currency });
  // Intl aceita texto decimal sem passar por float (ECMA-402 NumberFormat v3).
  return formatter.format(value.amount as unknown as number);
}
